import { SSOConnection } from '../../../api/sso';

/**
 * Manages UI state and business logic for SSO settings.
 * Separates state management from React components for better testability.
 */
export class SSOUIStateManager {
  /**
   * Determines if a connection should show the disable button.
   */
  public shouldShowDisableButton(connection: SSOConnection): boolean {
    return connection.status === 'ACTIVE';
  }

  /**
   * Determines if a connection should show the re-enable button.
   */
  public shouldShowReEnableButton(connection: SSOConnection): boolean {
    return connection.status === 'DISABLED';
  }

  /**
   * Checks if there's at least one disabled connection.
   */
  public hasDisabledConnection(connections: SSOConnection[]): boolean {
    return connections.some((conn) => conn.status === 'DISABLED');
  }

  /**
   * Checks if there's at least one active connection.
   */
  public hasActiveConnection(connections: SSOConnection[]): boolean {
    return connections.some((conn) => conn.status === 'ACTIVE');
  }

  /**
   * Determines if the enable form should be shown.
   * Shows form when:
   * - No connections exist, OR
   * - All connections are disabled and form is explicitly opened
   */
  public shouldShowEnableForm(
    connections: SSOConnection[],
    showFormFlag: boolean,
  ): boolean {
    if (connections.length === 0) {
      return showFormFlag;
    }

    // If there are connections but none are active, allow showing form
    if (!this.hasActiveConnection(connections)) {
      return showFormFlag;
    }

    return false;
  }

  /**
   * Determines if the "No SSO" section should be displayed.
   * Only show when there are truly no connections at all.
   * If there are disabled connections, don't show this section.
   */
  public shouldShowNoSSOSection(
    connections: SSOConnection[],
  ): boolean {
    return connections.length === 0;
  }

  /**
   * Determines if the connections list should be displayed.
   */
  public shouldShowConnectionsList(connections: SSOConnection[]): boolean {
    return connections.length > 0;
  }

  /**
   * Gets the appropriate button text based on connection state.
   */
  public getActionButtonText(connection: SSOConnection): string {
    return connection.status === 'ACTIVE' ? 'disable' : 're-enable';
  }

  /**
   * Formats connection status for display.
   */
  public formatConnectionStatus(status: string): string {
    return status.charAt(0) + status.slice(1).toLowerCase();
  }
}
